import { useRef, useState } from "react";
import { formatDate } from "../../api/dates";
import { useWorkbookImport } from "../../api/delivery";
import ReportView from "./ReportView";

/** When a workbook was last imported, as the server read it off what the
 * import left behind; `by` is null where the import records no account. */
export interface LastImport {
  at: string;
  by: string | null;
}

export interface LastImports {
  schedule: LastImport | null;
  approved_rates: LastImport | null;
}

/** The two workbooks that share the Upload screen: the client's project
 * schedule and the approved rate card. Each is one file, imported whole, with
 * the report of what it did shown under its card. */
export default function OtherImports({
  contracts,
  last,
}: {
  contracts: readonly { id: number; name: string }[];
  last: LastImports | undefined;
}) {
  return (
    <aside className="upload-side" aria-label="Other imports">
      <h2 className="section-label">Other imports</h2>
      <WorkbookCard
        title="Project schedule"
        path="schedule"
        contracts={contracts}
        last={last?.schedule}
        hint="The client's file, one sheet per region. Adds and updates sites on the schedule."
      />
      <WorkbookCard
        title="Approved rate card"
        path="approved-rates"
        contracts={contracts}
        last={last?.approved_rates}
        hint="The tracker workbook's 'Rate Card' sheet: which lines may be priced."
      />
    </aside>
  );
}

function lastText(last: LastImport | null | undefined): string | null {
  if (last === undefined) {
    return null;
  }
  if (last === null) {
    return "Not imported yet";
  }
  return `Last imported ${formatDate(last.at)}${last.by ? ` by ${last.by}` : ""}`;
}

function WorkbookCard({
  title,
  path,
  contracts,
  last,
  hint,
}: {
  title: string;
  path: "schedule" | "approved-rates";
  contracts: readonly { id: number; name: string }[];
  last: LastImport | null | undefined;
  hint: string;
}) {
  const run = useWorkbookImport(path);
  const [contractId, setContractId] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const contract = contractId || (contracts.length === 1 ? String(contracts[0].id) : "");
  const when = lastText(last);
  return (
    <section className="card card-pad upload-workbook">
      <strong>{title}</strong>
      <p>{hint}</p>
      {when ? <span className="upload-last">{when}</span> : null}
      {contracts.length > 1 ? (
        <label className="upload-contract">
          Contract
          <select className="select" value={contract} onChange={(e) => setContractId(e.target.value)} aria-label={`Contract for the ${title.toLowerCase()}`}>
            <option value="">Choose…</option>
            {contracts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <input
        ref={input}
        type="file"
        accept=".xlsx"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && contract !== "") {
            run.mutate({ contractId: Number(contract), file });
          }
          e.target.value = "";
        }}
      />
      <button type="button" className="btn btn-secondary" disabled={run.isPending || contract === ""} onClick={() => input.current?.click()}>
        {run.isPending ? "Importing…" : "Choose workbook (.xlsx)"}
      </button>
      {run.isError ? (
        <p className="notice bad" role="alert">
          {run.error.message}
        </p>
      ) : null}
      {run.data ? <ReportView report={run.data.report} /> : null}
    </section>
  );
}
